import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
/* Helper functions and constants */
import { getFormattedDate, ONE_DAY } from '../../../../helpers/dateTime';
import { CONTENT_TYPE } from '../widget-constants';
import { PERPETUAL, UNLIMITED } from "./licensing-constants";
/* Components */
import ProgressBar from '../../../common/progress-bar/ProgressBar';
import Table from '../../../common/table/table';
import WidgetEmpty from '../empty-widget-content/empty-widget-content';
import LicensingSolutionsList from './LicensingSolutionsList';
import { LICENSING_CONTENT_FULL_DATA_TEST_ID } from './licensing-constants';
/* Styles */
import './licensing.scss';

const TABLE_HEADER = ['Device Type', 'Count', 'License'];

const LicensingContent = ({ licensingData, errorMessage, isEditAllowed }) => {
    // INITIAL VALUES
    const [licenseUsagePercent, setLicenseUsagePercent] = useState(0);
    const [licenseDaysUsedPercent, setLicenseDaysUsedPercent] = useState(0);
    const [licenceUsageTitle, setLicenceUsageTitle] = useState('');
    const [licenceExpirationTitle, setLicenceExpirationTitle] = useState('');
    const [licenseDaysLeft, setLicenseDaysLeft] = useState(0);
    const [totalLicenses, setTotalLicences] = useState(0);
    const [inUseLicenses, setInUseLicenses] = useState(0)
    const [tableBodyData, setTableBodyData] = useState([
        ['0', ['Single license device', 0, 0]],
        ['1', ['Multi-license device', 0, 0]]
    ]);
    const [tableTotalData, setTableTotalData] = useState(['Total', 0, 0]);

    useEffect(() => {
        if (licensingData && licensingData.GLUWARE) {
            const { totalLicenses, expireDate, activeDate } = licensingData.GLUWARE;
            let { inUseLicenses, inUseSingleLicenseDevices, deviceCount } = licensingData.GLUWARE;

            if (totalLicenses === 0) {
                inUseLicenses = 0;
                inUseSingleLicenseDevices = 0;
                deviceCount = 0;
            }

            setTotalLicences(totalLicenses);
            setInUseLicenses(inUseLicenses);

            let licenseUsagePercent = totalLicenses === UNLIMITED
                ? UNLIMITED
                : getLicenseUsagePercent(inUseLicenses, totalLicenses)
            setLicenseUsagePercent(licenseUsagePercent);

            const licenceUsageTitle = totalLicenses === UNLIMITED
                ? 'License usage: unlimited'
                : `License usage: ${inUseLicenses} out of ${totalLicenses}`;
            setLicenceUsageTitle(licenceUsageTitle);

            const licenseDaysUsedPercent = expireDate === PERPETUAL
                ? UNLIMITED
                : getLicenseDaysUsedPercent(activeDate, expireDate)
            setLicenseDaysUsedPercent(licenseDaysUsedPercent);

            const licenceExpirationTitle = expireDate === PERPETUAL
                ? 'License expiration: unlimited'
                : `License expiration: ${getFormattedDate(expireDate, 'UTC')}`;
            setLicenceExpirationTitle(licenceExpirationTitle);

            const licenseDaysLeft = getDaysLeft(expireDate);
            setLicenseDaysLeft(licenseDaysLeft);

            const tableBodyData = [
                [
                    '0',
                    [
                        'Single license device',
                        inUseSingleLicenseDevices,
                        inUseSingleLicenseDevices
                    ]
                ],
                [
                    '1',
                    [
                        'Multi-license device',
                        deviceCount - inUseSingleLicenseDevices,
                        inUseLicenses - inUseSingleLicenseDevices
                    ]
                ]
            ];
            setTableBodyData(tableBodyData);

            const tableTotalData = [
                'Total',
                deviceCount,
                inUseLicenses
            ];
            setTableTotalData(tableTotalData);
        }
    }, [licensingData]);

    const getContentType = () => {
        if (
            !licensingData
            || errorMessage
            || (licensingData && !licensingData.GLUWARE)
        ) {
            return CONTENT_TYPE.ERROR;
        }
        return CONTENT_TYPE.DATA;
    };

    const getProgressColor = (usagePercent) => {
        if (usagePercent < 0) {
            return '#ccc';
        }
        if (usagePercent <= 80 || usagePercent === UNLIMITED) {
            return '#72B566';
        }
        if (usagePercent < 95) {
            return '#F4A83B';
        }

        return '#EC3323';
    };

    const getDaysLeft = (expireDate) => {
        return Math.floor((expireDate - Date.now()) / ONE_DAY);
    };

    const getSolutionProgressColor = (solution) => {
        const licensingFeedSolution = licensingData[solution];
        if (licensingFeedSolution && licensingFeedSolution.expireDate === PERPETUAL) {
            return getProgressColor(UNLIMITED);
        }
        const isSolutionDisabled = checkSolutionDisabled(solution);
        const usagePercentageForSolution = !isSolutionDisabled
            ? ((Date.now() - licensingFeedSolution.activeDate)
                / (licensingFeedSolution.expireDate
                    - licensingFeedSolution.activeDate))
            * 100
            : -1;
        return getProgressColor(usagePercentageForSolution);
    };

    const getSolutionLicenceExpirationTooltip = (solution) => {
        const licensingFeedSolution = licensingData[solution];

        //can be "PERPETUAL", milisec or undefined
        let expirationDate = licensingFeedSolution?.expireDate;
        let expiredSolution = false;
        if (expirationDate && typeof expirationDate === 'number') {
            if (expirationDate < Date.now()) {
                expiredSolution = true;
            }
            expirationDate = getFormattedDate(
                licensingFeedSolution.expireDate, 'UTC'
            );
        }

        const isSolutionDisabled = checkSolutionDisabled(solution);
        if (isSolutionDisabled) {
            if (expirationDate && expiredSolution) {
                return `No active license. Latest license has expired on ${expirationDate}`;
            }
            if (licensingFeedSolution?.error) {
                return 'No valid license';
            }
            return 'No active license';
        }

        if (licensingFeedSolution.expireDate === PERPETUAL) {
            return 'License expiration: unlimited';
        }

        const daysLeft = getDaysLeft(licensingFeedSolution.expireDate);
        return `License expiration: ${expirationDate} (${daysLeft} days left)`;
    };

    const getLicenseUsagePercent = (inUseLicenses, totalLicenses) => {
        const totalLicensesNum = totalLicenses === 0 ? 100 : ((inUseLicenses / totalLicenses) * 100);
        const controlNuber = totalLicensesNum % 1;
        return controlNuber === 0 ? totalLicensesNum : totalLicensesNum.toFixed(2);
    }

    const getLicenseDaysUsedPercent = (activeDate, expireDate) => {
        return Date.now() >= expireDate
            ? 100
            : (((Date.now() - activeDate) / (expireDate - activeDate)) * 100).toFixed(0);
    }

    const checkSolutionDisabled = (solution) => {
        if (!licensingData[solution] || licensingData[solution].error) {
            return true;
        }
        if (licensingData[solution].expireDate !== PERPETUAL && licensingData[solution].expireDate < Date.now()) {
            return true;
        }
        return false;
    };

    const renderContentType = () => {
        switch (getContentType()) {
            case CONTENT_TYPE.ERROR:
                return (
                    <WidgetEmpty
                        isEditAllowed={isEditAllowed}
                        text={errorMessage || 'Something went wrong.'}
                    />
                );
            case CONTENT_TYPE.DATA:
                return (
                    <div
                        data-testid={LICENSING_CONTENT_FULL_DATA_TEST_ID}
                        className="glu-content__section glu-content__separator glu-content__separator--inner glu-spread-v"
                    >
                        <ProgressBar
                            title={licenceUsageTitle}
                            progressColor={getProgressColor(licenseUsagePercent)}
                            progressPercent={
                                licenseUsagePercent
                            }
                            textLeft={
                                licenseUsagePercent !== UNLIMITED
                                    ? `${licenseUsagePercent}%`
                                    : ''
                            }
                            textRight={
                                licenseUsagePercent !== UNLIMITED
                                    ? `${Math.max(totalLicenses - inUseLicenses, 0)} unused`
                                    : UNLIMITED
                            }
                            customClassName="glu-progress-bar--first"
                        />

                        <ProgressBar
                            title={licenceExpirationTitle}
                            progressColor={getProgressColor(licenseDaysUsedPercent)}
                            progressPercent={
                                licenseDaysUsedPercent
                            }
                            textLeft={
                                licenseDaysUsedPercent !== UNLIMITED
                                    ? `${licenseDaysUsedPercent}%`
                                    : ''
                            }
                            textRight={
                                licenseDaysUsedPercent !== UNLIMITED
                                    ? `${Math.max(0, licenseDaysLeft)} days left`
                                    : UNLIMITED
                            }
                        />

                        <Table
                            tableTitle="License usage details"
                            tableHeader={TABLE_HEADER}
                            tableBodyData={tableBodyData}
                            tableTotalData={tableTotalData}
                        />
                        <LicensingSolutionsList
                            getSolutionLicenceExpirationTooltip={
                                getSolutionLicenceExpirationTooltip
                            }
                            getSolutionProgressColor={
                                getSolutionProgressColor
                            }
                            checkSolutionDisabled={checkSolutionDisabled}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            {renderContentType()}
        </>
    )
};

LicensingContent.propTypes = {
    isEditAllowed: PropTypes.bool,
    errorMessage: PropTypes.string,
    licensingData: PropTypes.objectOf(PropTypes.any)
};

LicensingContent.defaultProps = {
    isEditAllowed: false,
    errorMessage: '',
    licensingData: null
};

export default LicensingContent;
